/**
 * Branch/environment utilities for preview isolation
 *
 * Used to prefix LiveKit room names and other resources to isolate
 * different preview environments from each other.
 */

import { env } from "@repo/env";

import { isLocalHostname } from "./environment";

/**
 * Get a sanitized branch prefix for resource naming.
 * Returns empty string for production to keep room names clean.
 *
 * Examples:
 * - Production (terac.com): ""
 * - Main preview (preview.terac.com): "main"
 * - Feature branch (feat-voice.preview.terac.com): "feat-voice"
 * - Local dev: "dev"
 */
export function getBranchPrefix(): string {
  // Server-side: use VERCEL_GIT_COMMIT_REF
  if (typeof window === "undefined") {
    const vercelEnv = env.VERCEL_ENV;
    const gitRef = env.VERCEL_GIT_COMMIT_REF;

    // Production deployments don't get a prefix
    if (vercelEnv === "production") {
      return "";
    }

    // Preview deployments use the git branch name
    if (gitRef) {
      return sanitizeBranchName(gitRef);
    }

    // Local development
    if (env.NODE_ENV === "development") {
      return "dev";
    }

    return "";
  }

  // Client-side: use NEXT_PUBLIC env var or derive from hostname
  const publicBranch = env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_REF;
  if (publicBranch) {
    return sanitizeBranchName(publicBranch);
  }

  // Derive from hostname
  const hostname = window.location.hostname;

  // Production
  if (hostname === "terac.com" || hostname === "www.terac.com") {
    return "";
  }

  // Local dev (includes per-worktree *.localhost subdomains)
  if (isLocalHostname(hostname)) {
    return "dev";
  }

  // Main preview
  if (hostname === "preview.terac.com") {
    return "main";
  }

  // Feature branch preview: feat-voice.preview.terac.com -> "feat-voice"
  if (hostname.endsWith(".preview.terac.com")) {
    const branchPart = hostname.replace(".preview.terac.com", "");
    return sanitizeBranchName(branchPart);
  }

  // Vercel auto-generated URLs - extract branch if possible
  if (hostname.includes("vercel.app")) {
    // Can't reliably extract branch from Vercel URLs, use generic prefix
    return "preview";
  }

  return "";
}

/**
 * Sanitize a branch name for use in resource names (e.g., LiveKit rooms).
 * - Lowercase
 * - Replace non-alphanumeric with hyphens
 * - Limit length
 */
export function sanitizeBranchName(branch: string): string {
  return branch
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, "-")
    .replace(/--+/g, "-")
    .replace(/^-|-$/g, "")
    .substring(0, 20);
}

/**
 * Get the agent-pool name for explicit LiveKit dispatch.
 *
 * Server-side only (returns empty string in the browser).
 *
 * Composes the pool name from the branch prefix, matching the Python
 * worker's dispatch convention (`<base>` in prod, `<base>-preview-<branch>`
 * on PR previews). Both sides derive from the same git branch, so no env-var
 * coordination is needed between the web app and the agent worker.
 *
 * `LIVEKIT_AGENTS_NAME` is the explicit override consumed by every agent in
 * `apps/livekit-agents`. Set per dev in Infisical so each developer mints
 * tokens for their own local worker pool in the shared dev cluster. The
 * Python worker reads the same env var.
 */
export function getAgentName(base: string): string {
  if (typeof window !== "undefined") {
    return "";
  }

  const explicit = env.LIVEKIT_AGENTS_NAME;
  if (explicit) {
    return explicit;
  }

  const prefix = getBranchPrefix();
  return prefix ? `${base}-preview-${prefix}` : base;
}
